/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IColumn } from '@fluentui/react/lib/DetailsList';
import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { Table } from 'src/core/components/Table';
import { TableProps } from 'src/core/components/TableProps';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { AppVersionIdTableCell } from 'src/luis/appShell/managePane/appVersionsPage/components/AppVersionIdTableCell';
import { AppVersionPublishedDateTableCell } from 'src/luis/appShell/managePane/appVersionsPage/components/AppVersionPublishedDateTableCell';
import { AppVersionPublishState } from 'src/luis/projects/apps/types/AppVersionPublishState';
import { AppVersionStore } from 'src/luis/types/stores/apps/AppVersionStore';

export type AppVersionTableProps = TableProps<AppVersionStore> & {
    /**
     * Information of which version is active, published,
     * and on which slot and at what time.
     */
    versionsPublishState?: AppVersionPublishState;
};

const messages = defineMessages({
    nameColumnHeader: {
        id: 'apps.appVersionTable.nameColumnHeader',
        defaultMessage: 'Name'
    },
    publishedDateColumnHeader: {
        id: 'apps.appVersionTable.publishedDateColumnHeader',
        defaultMessage: 'Published Date'
    },
    createdDateColumnHeader: {
        id: 'apps.appVersionTable.createdDateColumnHeader',
        defaultMessage: 'Created Date'
    },
    lastModifiedDateColumnHeader: {
        id: 'apps.appVersionTable.lastModifiedDateColumnHeader',
        defaultMessage: 'Last Modified'
    }
});

/**
 * Displays a table of application versions.
 *
 * @param props The input properties for the table.
 */
export const AppVersionTable = (props: AppVersionTableProps) => {
    const { selection, items, versionsPublishState, sortKey, ...rest } = props;
    const { localize } = useLocalization();

    /**
     * App version table columns.
     */
    const columns: IColumn[] = [
        {
            key: 'id',
            name: localize(messages.nameColumnHeader),
            fieldName: 'id',
            minWidth: 200,
            maxWidth: 400,
            isRowHeader: true,
            isResizable: true,
            isCollapsible: false,
            isSorted: sortKey && !!(sortKey.key === 'id'),
            isSortedDescending: sortKey && !!(sortKey.key === 'id') && sortKey?.order === 'desc',
            onRender: (item: AppVersionStore, _, column) => (
                <AppVersionIdTableCell version={item} columnWidth={column.currentWidth} versionStates={versionsPublishState} />
            )
        },
        {
            key: 'publishedDate',
            name: localize(messages.publishedDateColumnHeader),
            fieldName: 'publishedDate',
            minWidth: 150,
            maxWidth: 200,
            isResizable: true,
            isCollapsible: false,
            isSorted: sortKey && !!(sortKey.key === 'publishedDate'),
            isSortedDescending: sortKey && !!(sortKey.key === 'publishedDate') && sortKey?.order === 'desc',
            onRender: (item: AppVersionStore, _, column) => (
                <AppVersionPublishedDateTableCell version={item} columnWidth={column.currentWidth} versionStates={versionsPublishState} />
            )
        },
        {
            key: 'createdDate',
            name: localize(messages.createdDateColumnHeader),
            fieldName: 'createdDate',
            minWidth: 150,
            maxWidth: 200,
            isResizable: true,
            isCollapsible: true,
            onRender: renderDate,
            isSorted: sortKey && !!(sortKey.key === 'createdDate'),
            isSortedDescending: sortKey && !!(sortKey.key === 'createdDate') && sortKey?.order === 'desc'
        },
        {
            key: 'modifiedDate',
            name: localize(messages.lastModifiedDateColumnHeader),
            fieldName: 'modifiedDate',
            minWidth: 150,
            isResizable: true,
            isCollapsible: true,
            onRender: renderDate,
            isSorted: sortKey && !!(sortKey.key === 'modifiedDate'),
            isSortedDescending: sortKey && !!(sortKey.key === 'modifiedDate') && sortKey?.order === 'desc'
        }
    ];

    return <Table<AppVersionStore> columns={columns} items={items} selection={selection} {...rest} />;
};

/**
 * Renders the date value as a string.
 *
 * @param item The item that is being rendered.
 * @param column The date column metadata
 */
const renderDate = (item: AppVersionStore, _: number, column: IColumn) => (
    <Stack verticalFill verticalAlign="center">
        {(item[column.fieldName as keyof AppVersionStore] as Date).toLocaleDateString()}
    </Stack>
);
